import { camelCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import { NursingArea } from '@pflegia/types/api/candidate/nursing-area';
import { getTypesOfWork, TypeOfWork } from '@pflegia/types/api/candidate/type-of-work';
import { FunnelQuestion, FunnelQuestionCategory } from '@pflegia/types/api/funnel/questions';
import { transformedEventProperties } from '@pflegia/ui/application/marketing/tracking/utils/transformed-event-properties';
import HeroButton from '@pflegia/ui/shared/hero-button/HeroButton';

import { useFunnelState } from '@funnel/hooks/use-funnel-state';

import { FUNNEL } from '@shared/application/constants';
import { getEventProperty } from '@shared/application/marketing/funnel-event-marketing-to-event-property';
import { funnelQuestionToFunnelEventMarketing } from '@shared/application/marketing/funnel-question-to-funnel-event-marketing';
import { useTracking } from '@shared/application/tracking.service';

const EmployerTypeButtons: React.FC = () => {
  const { t } = useTranslation('candidateFields', { keyPrefix: 'typesOfWork' });
  const { trackEventRegistration } = useTracking();
  const typesOfWork = getTypesOfWork(false);

  const setAnswers = useFunnelState(state => state.setAnswers);
  const handleNursingAreaClick = (typeOfWork: TypeOfWork) => {
    const nursingAreaQuestionId = FunnelQuestion.NursingAreaWorking;
    const nursingAreaEventName = funnelQuestionToFunnelEventMarketing[nursingAreaQuestionId];
    const employeeTypeQuestionId = FunnelQuestion.EmployeeType;
    const employeeTypeEventName = funnelQuestionToFunnelEventMarketing[employeeTypeQuestionId];
    const nursingArea = mapTypeOfWorkToNursingArea(typeOfWork);
    trackEventRegistration(
      employeeTypeEventName,
      transformedEventProperties({
        [getEventProperty(employeeTypeEventName)]: typeOfWork,
        [getEventProperty(nursingAreaEventName)]: nursingArea,
      }),
      employeeTypeQuestionId,
      FunnelQuestionCategory.Field,
    );

    setAnswers({ [FunnelQuestion.NursingAreaWorking]: nursingArea, [FunnelQuestion.EmployeeType]: [typeOfWork] });
  };

  return (
    <div className='grid grid-cols-1 justify-center gap-2 lg:grid-cols-2'>
      {typesOfWork.map(typeOfWork => (
        <HeroButton
          dataTestId={`type-of-work-button-${typeOfWork}`}
          href={{ pathname: FUNNEL, query: { id: FunnelQuestion.JobTitle } }}
          key={typeOfWork}
          onClick={() => handleNursingAreaClick(typeOfWork)}>
          {t(camelCase(typeOfWork))}
        </HeroButton>
      ))}
    </div>
  );
};

const mapTypeOfWorkToNursingArea = (typeOfWork: TypeOfWork): NursingArea => {
  switch (typeOfWork) {
    case TypeOfWork.NursingHome:
      return NursingArea.ElderlyCare;
    case TypeOfWork.Ambulant:
    case TypeOfWork.Hospital:
    case TypeOfWork.Intensive:
      return NursingArea.Nursing;
    case TypeOfWork.DentalPractice:
    case TypeOfWork.DoctorOffice:
    case TypeOfWork.Physiotherapy:
      return NursingArea.MedicalStaff;
    default:
      return NursingArea.Other;
  }
};

export default EmployerTypeButtons;
